import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';
import NotFoundPage from './NotFoundPage';
import { getCoverUrl, getOgImageUrl, resolveUploadUrls } from '../utils/newsImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? `${window.location.protocol}//${window.location.hostname}:8001`;

const CATEGORY_COLORS = {
  PARCHE:  { bg: 'rgba(0,120,255,0.18)',  border: 'rgba(0,120,255,0.45)',  text: '#69CCF0' },
  EVENTO:  { bg: 'rgba(255,140,0,0.15)',  border: 'rgba(255,140,0,0.45)',  text: '#FFAA44' },
  PVP:     { bg: 'rgba(200,30,30,0.15)',  border: 'rgba(200,30,30,0.45)',  text: '#FF6666' },
  AVISO:   { bg: 'rgba(160,50,255,0.15)', border: 'rgba(160,50,255,0.45)', text: '#B080FF' },
};

function buildNewsArticleSchema(noticia) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: noticia.seo_title || noticia.title,
    description: noticia.seo_description || noticia.excerpt,
    image: getOgImageUrl(noticia),
    datePublished: noticia.published_at,
    dateModified: noticia.updated_at || noticia.published_at,
    author: { '@type': 'Person', name: noticia.author || 'Staff Frostbound' },
    publisher: {
      '@type': 'Organization',
      name: 'Frostbound',
      logo: { '@type': 'ImageObject', url: 'https://www.frostbound.org/favicon.png' },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.frostbound.org/noticias/${noticia.slug}` },
  };
}

export default function NoticiasDetailPage() {
  const { slug } = useParams();
  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${BACKEND_URL}/api/news/${slug}`)
      .then(({ data }) => setNoticia(data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (!loading && notFound) return <NotFoundPage />;

  const catStyle = noticia ? (CATEGORY_COLORS[noticia.category] || CATEGORY_COLORS.AVISO) : null;

  return (
    <PageWrapper scrollable>
      {noticia && (
        <SEOMeta
          title={noticia.seo_title || noticia.title}
          description={noticia.seo_description || noticia.excerpt}
          canonical={`/noticias/${noticia.slug}`}
          ogImage={getOgImageUrl(noticia)}
          ogType="article"
          articlePublishedTime={noticia.published_at}
          articleModifiedTime={noticia.updated_at || noticia.published_at}
          jsonLd={buildNewsArticleSchema(noticia)}
        />
      )}
      <Navbar />

      <main style={{ flex: 1, maxWidth: 860, margin: '0 auto', padding: '2rem 1.5rem', width: '100%' }}>
        {/* Breadcrumb */}
        <nav aria-label="Migas de pan" style={{ marginBottom: '1.5rem', fontSize: '0.85rem', color: '#8899aa' }}>
          <Link to="/" style={{ color: '#69CCF0', textDecoration: 'none' }}>Inicio</Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          <Link to="/noticias" style={{ color: '#69CCF0', textDecoration: 'none' }}>Noticias</Link>
          {noticia && <><span style={{ margin: '0 0.5rem' }}>›</span><span>{noticia.title}</span></>}
        </nav>

        {loading && (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#8899aa' }}>Cargando noticia…</div>
        )}

        {noticia && (
          <article>
            {/* Category badge */}
            {noticia.category && (
              <span style={{
                display: 'inline-block',
                padding: '0.2rem 0.8rem',
                borderRadius: 20,
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                marginBottom: '1rem',
                background: catStyle.bg,
                border: `1px solid ${catStyle.border}`,
                color: catStyle.text,
              }}>
                {noticia.category}
              </span>
            )}

            <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', fontWeight: 700, color: '#e8f4ff', marginBottom: '0.75rem', lineHeight: 1.3 }}>
              {noticia.title}
            </h1>

            {/* Meta row */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#8899aa', marginBottom: '1.5rem', alignItems: 'center' }}>
              {noticia.published_at && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Calendar size={14} /> {noticia.published_at}
                </span>
              )}
              {noticia.author && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <User size={14} /> {noticia.author}
                </span>
              )}
            </div>

            {/* Cover image */}
            <img
              src={getCoverUrl(noticia)}
              alt={noticia.title}
              loading="lazy"
              decoding="async"
              style={{ width: '100%', maxHeight: 400, objectFit: 'cover', borderRadius: 10, marginBottom: '1.5rem', border: '1px solid rgba(255,255,255,0.07)' }}
              onError={e => { if (e.target.src.includes('/news-fallbacks/')) { e.target.style.display = 'none'; } else { e.target.src = '/news-fallbacks/default.jpg'; } }}
            />

            {/* Excerpt */}
            {noticia.excerpt && (
              <p style={{ fontSize: '1.05rem', color: '#a8c0d8', marginBottom: '1.5rem', lineHeight: 1.7, fontStyle: 'italic', borderLeft: '3px solid #69CCF0', paddingLeft: '1rem' }}>
                {noticia.excerpt}
              </p>
            )}

            {/* Content */}
            {noticia.content && (
              <div
                className="news-content"
                style={{ color: '#c0d8f0', lineHeight: 1.8, fontSize: '0.97rem' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolveUploadUrls(noticia.content), { USE_PROFILES: { html: true } }) }}
              />
            )}

            {/* Back link */}
            <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <Link
                to="/noticias"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#69CCF0', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 500 }}
              >
                <ArrowLeft size={16} /> Volver a noticias
              </Link>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </PageWrapper>
  );
}
